'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiService } from '@/_services/api.service';
import { queryKeys } from '../queries/queryKeys';
import type { IConversation } from '@/types';
import { AnalyticsService } from '@/_services/analytics.service.client';

/**
 * Mutation hook to toggle conversation visibility (public/private)
 *
 * Features:
 * - Optimistic update on conversation detail cache
 * - Invalidates all conversation pages on success
 * - Error rollback with toast notification
 * - Success toast for visibility changes
 */
export const useUpdateVisibility = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, isPublic }: { id: string; isPublic: boolean }) => {
			const { data, error } = await ApiService.UpdateConversationVisibility(id, isPublic);
			if (error || !data) throw new Error(error?.message || 'Failed to update visibility');
			return data;
		},
		onMutate: async ({ id, isPublic }) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({ queryKey: queryKeys.conversations.detail(id) });
			await queryClient.cancelQueries({ queryKey: queryKeys.conversations.root });

			// Snapshot current state for rollback
			const previousConversation = queryClient.getQueryData<IConversation>(queryKeys.conversations.detail(id));

			// Optimistic update on detail
			if (previousConversation) {
				queryClient.setQueryData<IConversation>(queryKeys.conversations.detail(id), {
					...previousConversation,
					isPublic
				});
			}

			return { previousConversation };
		},
		onError: (_err, { id }, context) => {
			// Rollback on error
			if (context?.previousConversation) {
				queryClient.setQueryData(queryKeys.conversations.detail(id), context.previousConversation);
			}
			toast.error('Failed to update visibility');
		},
		onSuccess: (conversation) => {
			// Invalidate all conversation pages to refresh the list
			queryClient.invalidateQueries({ queryKey: queryKeys.conversations.root });

			if (conversation.isPublic) {
				AnalyticsService.conversationShared({ conversationId: conversation.id });
				toast.success('Conversation is now public');
			} else {
				toast.success('Conversation is now private');
			}
		}
	});
};
